import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import Table from "../../components/UI/CommonTable/CommonTable";
import Pagination from "../../components/UI/List/Pagination";
import ConfirmationModal from "../../components/UI/Modal/ConfirmationModal";
import useConfirmationModal from "../../hooks/useConfirmationModal";
import { callApi } from "../../api/callApi";
import { API_ENDPOINT, API_URL_2 } from "../../api/constant";
import { showToastNotification } from "../../utility/helper";
import { IModifierGroup } from "../../interfaces/modifier";
import moment from "../../utility/moment-timezone";

const ModifierGroups: React.FC = () => {
    const [groups, setGroups] = useState<IModifierGroup[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const { showModal, modalProps, confirmAction } = useConfirmationModal();
    const navigate = useNavigate();

    useEffect(() => {
        getGroups(currentPage);
    }, [currentPage]);

    const getGroups = async (page: number = 1) => {
        try {
            setLoading(true);
            const res = await callApi(
                `${API_URL_2}${API_ENDPOINT.page}modifier?page=${page}`,
                { method: "GET" }
            );
            // Response shape is normalised defensively (array | {data,...} | {data,pageInfo})
            const list: IModifierGroup[] = Array.isArray(res) ? res : res?.data || [];
            setGroups(list);
            setCurrentPage(res?.currentPage ?? res?.pageInfo?.page ?? page);
            setTotalPages(res?.totalPages ?? res?.pageInfo?.totalPages ?? 1);
        } catch (error: any) {
            console.error("Failed to fetch modifier groups:", error.message);
            showToastNotification("danger", "Failed to fetch modifier groups");
        } finally {
            setLoading(false);
        }
    };

    const deleteGroup = async (id: string) => {
        try {
            await callApi(`${API_URL_2}${API_ENDPOINT.page}modifier/${id}`, { method: "DELETE" });
            showToastNotification("success", "Modifier group deleted successfully");
            getGroups(currentPage);
        } catch (error: any) {
            console.error("Failed to delete modifier group:", error.message);
            showToastNotification("danger", error.message || "Failed to delete modifier group");
        }
    };

    const handleDeleteClick = (id: string) => {
        confirmAction({
            title: "Confirm Delete",
            message: "Are you sure you want to delete this modifier group?",
            onConfirm: () => deleteGroup(id),
        });
    };

    const columns = [
        { header: "Name", accessor: "name" },
        {
            header: "Collections",
            accessor: "modifiers",
            render: (_: any, group: IModifierGroup) => {
                const collectionsList =
                    group.modifiers && Array.isArray(group.modifiers) && group.modifiers.length > 0
                        ? group.modifiers
                        : group.modifier
                            ? [group.modifier]
                            : [];

                if (collectionsList.length > 0) {
                    return (
                        <span>
                            {collectionsList.map((c: any) => c.label).filter(Boolean).join(", ")}
                        </span>
                    );
                }
                return <span className="text-muted">-</span>;
            },
        },
        {
            header: "Options",
            accessor: "options",
            render: (options: any[]) => options?.length ?? 0,
        },
        {
            header: "Status",
            accessor: "isActive",
            render: (isActive: boolean) => (
                <span className={`badge ${isActive ? "bg-success" : "bg-danger"}`}>
                    {isActive ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            header: "Created At",
            accessor: "createdAt",
            render: (date: string) => (date ? moment(date).format("DD MMM, YYYY") : "-"),
        },
    ];

    const actions = [
        {
            icon: "bi bi-pen",
            color: "success",
            tooltip: "Edit & manage options",
            onClick: (group: IModifierGroup) => navigate(`/modifiers/edit/${group.id}`),
        },
        {
            icon: "bi bi-trash3",
            color: "danger",
            tooltip: "Delete",
            onClick: (group: IModifierGroup) => handleDeleteClick(group.id),
        },
    ];

    return (
        <Layout>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
                <h1 className="app-page-title mb-0">Modifier Groups</h1>
                <Link to="/modifiers/add" className="btn app-btn-primary d-inline-flex align-items-center gap-1 text-nowrap">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M6 0C6.13261 0 6.25979 0.0526785 6.35355 0.146447C6.44732 0.240215 6.5 0.367392 6.5 0.5V5.5H11.5C11.6326 5.5 11.7598 5.55268 11.8536 5.64645C11.9473 5.74021 12 5.86739 12 6C12 6.13261 11.9473 6.25979 11.8536 6.35355C11.7598 6.44732 11.6326 6.5 11.5 6.5H6.5V11.5C6.5 11.6326 6.44732 11.7598 6.35355 11.8536C6.25979 11.9473 6.13261 12 6 12C5.86739 12 5.74021 11.9473 5.64645 11.8536C5.55268 11.7598 5.5 11.6326 5.5 11.5V6.5H0.5C0.367392 6.5 0.240215 6.44732 0.146447 6.35355C0.0526785 6.25979 0 6.13261 0 6C0 5.86739 0.0526785 5.74021 0.146447 5.64645C0.240215 5.55268 0.367392 5.5 0.5 5.5H5.5V0.5C5.5 0.367392 5.55268 0.240215 5.64645 0.146447C5.74021 0.0526785 5.86739 0 6 0Z" fill="white" />
                    </svg>
                    Add Modifier Group
                </Link>
            </div>

            <div className="app-card app-card-orders-table shadow-sm mb-4 p-3 p-md-4">
                <div className="app-card-body">
                    <Table
                        columns={columns}
                        data={groups}
                        loading={loading}
                        actions={actions}
                        emptyMessage="No modifier groups available."
                    />
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                </div>
            </div>
            <ConfirmationModal {...modalProps} show={showModal} />
        </Layout>
    );
};

export default ModifierGroups;
